import React, { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AccountProvider } from './context/AccountContext'
import { AuthProvider, useAuth } from './context/AuthContext'
import { CredentialSetProvider } from './context/CredentialSetContext'
import { Layout } from './components/Layout'

// Eager load Login page (needed immediately)
import Login from './pages/Login'

// Lazy load all other pages for code splitting
const Dashboard = lazy(() => import('./pages/Dashboard'))
const Backtesting = lazy(() => import('./pages/Backtesting'))
const BacktestResults = lazy(() => import('./pages/BacktestResults'))
const Downloads = lazy(() => import('./pages/Downloads'))
const Instruments = lazy(() => import('./pages/Instruments'))
const InstrumentLists = lazy(() => import('./pages/InstrumentLists'))
const Strategies = lazy(() => import('./pages/Strategies'))
const ChartPage = lazy(() => import('./pages/ChartPage').then(module => ({ default: module.ChartPage })))
const OptimizationWizard = lazy(() => import('./pages/OptimizationWizard'))
const Optimizations = lazy(() => import('./pages/Optimizations'))
const CompareOptimizations = lazy(() => import('./pages/CompareOptimizations'))
const OptimizationResults = lazy(() => import('./pages/OptimizationResults'))
const WalkForwardResults = lazy(() => import('./pages/WalkForwardResults'))
const MonteCarloResults = lazy(() => import('./pages/MonteCarloResults'))
const PerformancePage = lazy(() => import('./pages/PerformancePage'))
const InvestorsPage = lazy(() => import('./pages/InvestorsPage'))

// Loading fallback component
const PageLoader: React.FC = () => (
  <div className="flex items-center justify-center min-h-screen bg-gray-100">
    <div className="text-center">
      <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
)

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth()

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>
  }

  return isAuthenticated ? <Layout>{children}</Layout> : <Navigate to="/login" />
}

function App() {
  return (
    <BrowserRouter>
      <AccountProvider>
        <AuthProvider>
          <CredentialSetProvider>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route
                  path="/dashboard"
                  element={
                    <PrivateRoute>
                      <Dashboard />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/backtesting"
                  element={
                    <PrivateRoute>
                      <Backtesting />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/backtests/:id"
                  element={
                    <PrivateRoute>
                      <BacktestResults />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/backtests/portfolio/:id"
                  element={
                    <PrivateRoute>
                      <BacktestResults />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/downloads"
                  element={
                    <PrivateRoute>
                      <Downloads />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/instruments"
                  element={
                    <PrivateRoute>
                      <Instruments />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/instrument-lists"
                  element={
                    <PrivateRoute>
                      <InstrumentLists />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/strategies"
                  element={
                    <PrivateRoute>
                      <Strategies />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/performance"
                  element={
                    <PrivateRoute>
                      <PerformancePage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/charts/:instrumentId?"
                  element={
                    <PrivateRoute>
                      <ChartPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/optimization/wizard"
                  element={
                    <PrivateRoute>
                      <OptimizationWizard />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/optimizations"
                  element={
                    <PrivateRoute>
                      <Optimizations />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/optimizations/compare"
                  element={
                    <PrivateRoute>
                      <CompareOptimizations />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/optimization/:runId"
                  element={
                    <PrivateRoute>
                      <OptimizationResults />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/optimization/walk-forward/:runId"
                  element={
                    <PrivateRoute>
                      <WalkForwardResults />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/optimization/monte-carlo/:runId"
                  element={
                    <PrivateRoute>
                      <MonteCarloResults />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/investors"
                  element={
                    <PrivateRoute>
                      <InvestorsPage />
                    </PrivateRoute>
                  }
                />
                <Route path="/" element={<Navigate to="/dashboard" />} />
              </Routes>
            </Suspense>
          </CredentialSetProvider>
        </AuthProvider>
      </AccountProvider>
    </BrowserRouter>
  )
}

export default App
